import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { githubCopy } from '@/copy'
import { githubAppApi, takeHeldInstallFlow } from '../api/githubApp'
import { GitHubAppSetupView } from '../components/GitHubAppSetupView'
import { ROUTES } from '../constants/routes'

/**
 * The central GitHub App's callback URL: GitHub lands the browser here with
 * `installation_id`, a user-authorization `code`, and the flow `state`
 * after the user installs and authorizes the App. The claim runs as the
 * signed-in user (only whoever started the flow can complete it) and the
 * code proves their GitHub account can access the installation.
 */
export function GitHubAppInstalledPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const installationId = params.get('installation_id')
  const code = params.get('code')
  const echoedState = params.get('state')
  const [error, setError] = useState<string | null>(null)
  // The claim is not idempotent: guard against StrictMode's double effect.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const held = takeHeldInstallFlow()
    // GitHub echoes the flow's state; it must be the flow this browser
    // started. Otherwise a crafted callback link (someone else's
    // installation and code) could complete this user's pending flow and
    // pipe a foreign installation into their workspace.
    const state = echoedState
    // Validation failures surface through the same promise chain as claim
    // failures, so the effect never sets state synchronously.
    Promise.resolve()
      .then(() => {
        if (!state || !installationId || held?.state !== state) {
          throw new Error(githubCopy.installed.noFlow)
        }
        if (!code) {
          throw new Error(githubCopy.installed.noAuthorization)
        }
        return githubAppApi
          .claimInstall(installationId, state, code)
          .catch((e: unknown) => {
            throw new Error(
              githubCopy.installed.claimFailed(
                e instanceof Error ? e.message : String(e),
              ),
            )
          })
      })
      .then((done) =>
        // The connection's page: its endpoint, and what uses it (recipes).
        navigate(ROUTES.resourceDetail(done.connection_id), { replace: true }),
      )
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
  }, [installationId, code, echoedState, navigate])

  return <GitHubAppSetupView error={error} />
}
